import { ReactNode, useEffect, useState } from 'react';
import { get } from 'lodash';
import * as R from 'remeda';
import { closestCenter, DndContext, DragEndEvent } from '@dnd-kit/core';
import {
  restrictToParentElement,
  restrictToVerticalAxis,
} from '@dnd-kit/modifiers';
import {
  SortableContext,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { FormattedMessage } from 'react-intl';
import { from, fromEvent, of } from 'rxjs';
import { catchError, filter, map, tap } from 'rxjs/operators';
import {
  IconCirclePlus,
  IconClipboard,
  IconCopy,
  IconInfoSquareRounded,
  IconTrash,
} from '@rossum/ui/icons/tabler';
import {
  Box,
  Chip,
  IconButton,
  Stack,
  SvgIcon,
  Tooltip,
  Typography,
} from '@rossum/ui/material';
import { ENUM_EDITOR_LINK } from '../../../../../../constants/values';
import { getEventPath } from '../../../../../../lib/DOM';
import { linebreak, link, white } from '../../../../../../lib/formaterValues';
import { C_KEY_CODE, V_KEY_CODE } from '../../../../../../lib/keyboard';
import {
  insertInArray,
  swapItemsInArray,
  updateInArray,
} from '../../../../../../redux/modules/utils';
import { EnumOption } from '../../../../../../types/schema';
import { useOpenModal } from '../../../../../../utils/hooks/useOpenModal';
import { useCopyPastePermissions } from '../../lib/enumEditorHooks';
import EnumEditorItem from './EnumEditorItem';
import { OPTION_INPUT_WIDTH, optionRowSx } from './enumEditorSx';

const defaultOption = { value: '', label: '' };

const isLabel = <T extends EventTarget>(path: T[]) =>
  path.some(
    el =>
      'className' in el &&
      typeof el.className === 'string' &&
      el.className.includes('LabelInput')
  );

const textToOptions = (text: string) =>
  R.pipe(
    text,
    R.split('\n'),
    R.map(row => {
      const option = row.split('\t');
      return { value: get(option, '[0]', ''), label: get(option, '[1]', '') };
    }),
    R.filter(item => {
      return !(R.isEmpty(item.value) && R.isEmpty(item.label));
    })
  );

type Props = {
  options: Array<EnumOption>;
  onCurrentSchemaPartChange: (_options: Array<EnumOption>) => void;
};

const labelSx = {
  display: 'flex',
  width: OPTION_INPUT_WIDTH,
  alignItems: 'center',
  pl: '22px',
};

const EnumEditor = ({ options, onCurrentSchemaPartChange }: Props) => {
  const [pasteIsHovered, setPasteIsHovered] = useState<boolean>(false);
  const [clipboardText, setClipboardText] = useState<string>('');
  const [optionsVersion, setOptionsVersion] = useState<number>(Date.now);
  const [previewOptions, setPreviewOptions] = useState<Array<EnumOption>>([]);
  const [currentLine, setCurrentLine] = useState<number | undefined>(undefined);

  const { pasteIsAllowed, pasteIsSupported } = useCopyPastePermissions();

  const [DialogElement, openModal] = useOpenModal();

  useEffect(() => {
    const clipboardTextSubscription =
      pasteIsAllowed &&
      from(navigator.clipboard.readText())
        .pipe(catchError(() => of('')))
        .subscribe((text: string) => setClipboardText(text));

    return () => {
      if (clipboardTextSubscription) clipboardTextSubscription.unsubscribe();
    };
  });

  useEffect(
    () => setPreviewOptions(!clipboardText ? [] : textToOptions(clipboardText)),
    [clipboardText]
  );

  const showPreviewOption = pasteIsHovered && previewOptions.length;
  const currentOptions = showPreviewOption ? previewOptions : options;
  const emptyOptions = !currentOptions.length;
  const withTmpOption = emptyOptions ? [] : currentOptions;
  const sortableOptions = withTmpOption.map(
    (item, index) => `${item.value}-${item.label}-${index}`
  );

  useEffect(() => {
    if (!currentLine) return undefined;

    const copySubscription = fromEvent<KeyboardEvent>(window, 'keydown')
      .pipe(
        filter(e => e.metaKey || e.ctrlKey),
        filter(({ keyCode }) => keyCode === C_KEY_CODE),
        map(getEventPath),
        map(path => {
          const option = withTmpOption[currentLine];
          return option?.[isLabel(path) ? 'label' : 'value'] ?? '';
        })
      )
      .subscribe((value: string) =>
        navigator.clipboard.writeText(value).catch(() => {})
      );
    return () => {
      copySubscription.unsubscribe();
    };
  });

  useEffect(() => {
    if (!currentLine) return undefined;

    const pasteSubscription = fromEvent<KeyboardEvent>(window, 'keydown')
      .pipe(
        filter(e => e.metaKey || e.ctrlKey),
        filter(({ keyCode }) => keyCode === V_KEY_CODE),
        filter(() => pasteIsAllowed),
        filter(() => !!clipboardText),
        tap(event => event.preventDefault()),
        map(getEventPath)
      )
      .subscribe(path => {
        if (clipboardText.includes('\n')) {
          const newOptions = textToOptions(clipboardText);
          const headOptions = R.take(options, currentLine + 1) || [];
          onCurrentSchemaPartChange([...headOptions, ...newOptions]);
        } else if (clipboardText.includes('\t')) {
          const option = clipboardText.split('\t');
          const newOption = {
            value: get(option, '[0]', ''),
            label: get(option, '[1]', ''),
          };
          onCurrentSchemaPartChange(
            updateInArray(options, currentLine, _option => newOption)
          );
        } else
          onCurrentSchemaPartChange(
            updateInArray(options, currentLine, _option => ({
              ..._option,
              [isLabel(path) ? 'label' : 'value']: clipboardText,
            }))
          );
      });

    return () => {
      pasteSubscription.unsubscribe();
    };
  });

  useEffect(() => {
    const onBlurSubscription = fromEvent(window, 'click')
      .pipe(
        filter(() => optionsVersion !== undefined),
        filter(
          event =>
            !getEventPath(event).some(element =>
              ['addOption', 'option'].includes(
                'id' in element && typeof element.id === 'string'
                  ? element.id
                  : ''
              )
            )
        )
      )
      .subscribe(() => setCurrentLine(undefined));
    return () => onBlurSubscription.unsubscribe();
  });

  const reRenderInputs = () => setOptionsVersion(Date.now);

  useEffect(() => {
    if (currentLine !== undefined && currentLine > withTmpOption.length - 1) {
      setCurrentLine(undefined);
    }
  }, [currentLine, options.length, withTmpOption.length]);

  const addLine = () => {
    const index = currentLine ? currentLine + 1 : options.length;

    onCurrentSchemaPartChange(insertInArray(options, index, defaultOption));

    setCurrentLine(index);
    reRenderInputs();
  };

  const onCopy = () => {
    const optionsText = options
      .map(({ value, label: _label }) => `${value}\t${_label}`)
      .join('\n');
    navigator.clipboard.writeText(optionsText).catch(() => {});
  };

  const onPaste = () => {
    if (pasteIsHovered && previewOptions.length) {
      onCurrentSchemaPartChange(previewOptions);
    }
  };

  const removeAll = () => {
    onCurrentSchemaPartChange([]);
  };

  const handleDragEnd = ({ active, over }: DragEndEvent) => {
    if (over && active.id !== over.id) {
      const activeFieldIndex = sortableOptions.indexOf(`${active.id}`);
      const overFieldIndex = sortableOptions.indexOf(`${over.id}`);

      onCurrentSchemaPartChange(
        swapItemsInArray(options, activeFieldIndex, overFieldIndex)
      );
    }
  };

  const pasteTooltip = pasteIsAllowed ? (
    previewOptions.length ? (
      <FormattedMessage
        id="components.enumEditor.paste"
        values={{
          linebreak,
          hint: (msg: ReactNode) => <span>{msg}</span>,
        }}
      />
    ) : (
      <FormattedMessage id="components.enumEditor.noContent" />
    )
  ) : pasteIsSupported ? (
    <FormattedMessage id="components.enumEditor.pasteIsNotAllowed" />
  ) : (
    <FormattedMessage id="components.enumEditor.pasteIsNotSupported" />
  );

  return (
    <Stack sx={{ mb: 3 }}>
      <Stack
        direction="row"
        sx={{ justifyContent: 'space-between', py: '20px' }}
      >
        <Stack
          direction="row"
          spacing={1}
          useFlexGap
          sx={{ alignItems: 'center' }}
        >
          <Typography variant="subtitle2" color="text.primary">
            <FormattedMessage id="components.enumEditor.enumOptions" />
          </Typography>
          <Chip
            label={showPreviewOption ? previewOptions.length : options.length}
            size="small"
          />
          <Tooltip
            title={
              <FormattedMessage
                id="components.enumEditor.helpTooltip"
                values={{ link: link(ENUM_EDITOR_LINK), linebreak }}
              />
            }
          >
            <SvgIcon fontSize="large" color="action">
              <IconInfoSquareRounded />
            </SvgIcon>
          </Tooltip>
        </Stack>
        <Stack direction="row" spacing={0.5} alignItems="center">
          <Tooltip
            title={<FormattedMessage id="components.enumEditor.addItem" />}
          >
            <IconButton size="small" onClick={addLine}>
              <SvgIcon fontSize="medium">
                <IconCirclePlus />
              </SvgIcon>
            </IconButton>
          </Tooltip>
          <Tooltip
            title={<FormattedMessage id="components.enumEditor.copyToClip" />}
          >
            <IconButton size="small" onClick={onCopy}>
              <SvgIcon fontSize="medium">
                <IconCopy />
              </SvgIcon>
            </IconButton>
          </Tooltip>
          <div
            onMouseEnter={() => pasteIsAllowed && setPasteIsHovered(true)}
            onMouseLeave={() => pasteIsAllowed && setPasteIsHovered(false)}
          >
            <Tooltip
              key={`{pasteTooltip-${!!previewOptions}`}
              title={previewOptions === undefined ? '' : pasteTooltip}
            >
              <IconButton
                size="small"
                onClick={() => pasteIsAllowed && onPaste()}
                sx={{
                  cursor:
                    !pasteIsAllowed || !previewOptions.length
                      ? 'not-allowed'
                      : 'pointer',
                }}
              >
                <SvgIcon fontSize="medium">
                  <IconClipboard />
                </SvgIcon>
              </IconButton>
            </Tooltip>
          </div>
          <Tooltip
            title={<FormattedMessage id="components.enumEditor.deleteAll" />}
          >
            <IconButton
              color="error"
              size="small"
              onClick={() => {
                if (emptyOptions) return;

                openModal({
                  textId: 'enumEditor.removeAll',
                  onConfirm: removeAll,
                  values: {
                    white,
                  },
                });
              }}
              sx={{ cursor: emptyOptions ? 'not-allowed' : 'pointer' }}
            >
              <SvgIcon fontSize="medium">
                <IconTrash />
              </SvgIcon>
            </IconButton>
          </Tooltip>
        </Stack>
      </Stack>
      <Box
        sx={{
          pb: '20px',
          overflowY: 'auto',
          height: 280,
        }}
      >
        <div>
          {!!withTmpOption.length && (
            <Stack direction="row" sx={optionRowSx}>
              <Typography variant="caption" color="text.secondary" sx={labelSx}>
                <FormattedMessage id="components.enumEditor.id" />
                <Tooltip
                  title={
                    <FormattedMessage id="components.enumEditor.id.tooltip" />
                  }
                >
                  <SvgIcon fontSize="medium" sx={{ ml: '8px' }}>
                    <IconInfoSquareRounded />
                  </SvgIcon>
                </Tooltip>
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={labelSx}>
                <FormattedMessage id="components.enumEditor.label" />
              </Typography>
            </Stack>
          )}
          <DndContext
            onDragEnd={handleDragEnd}
            collisionDetection={closestCenter}
            modifiers={[restrictToVerticalAxis, restrictToParentElement]}
          >
            <SortableContext
              items={sortableOptions}
              strategy={verticalListSortingStrategy}
            >
              {withTmpOption.map((option: EnumOption, i: number) => (
                <EnumEditorItem
                  // oxlint-disable-next-line react/no-array-index-key
                  key={i}
                  enumId={sortableOptions[i] ?? ''}
                  enumIndex={i}
                  enumItem={option}
                  optionsAreEmpty={emptyOptions}
                  options={options}
                  currentLine={currentLine}
                  setCurrentLine={setCurrentLine}
                  onCurrentSchemaPartChange={onCurrentSchemaPartChange}
                />
              ))}
            </SortableContext>
          </DndContext>
        </div>
      </Box>
      {DialogElement}
    </Stack>
  );
};

export default EnumEditor;
